import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Button, Switch, FormControlLabel,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress, Alert, Divider, Paper,
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Google as GoogleIcon,
  Folder as FolderIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Backup as BackupIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import settingsService from '../services/settingsService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';


export default function BackupTab({ backupForm = {}, setBackupForm }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [driveStatus, setDriveStatus] = useState({ connected: false, email: null, folderId: '' });
  const [lastBackup, setLastBackup] = useState(null); // ISO timestamp or null
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { t } = useTranslation();
  const [alert, setAlert] = useState(null); // { severity, message }

  // ── Manual download date range ─────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().slice(0, 10);
  const [downloadRange, setDownloadRange] = useState({ from: firstOfMonth, to: today });



  // ── load Drive status + existing backup settings ──────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const statusRes = await settingsService.getDriveStatus();
      setDriveStatus(statusRes);
      setLastBackup(statusRes?.lastBackupAt ?? null);
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to load backup settings.' });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  // ── handle ?driveConnected=true|false after OAuth redirect ────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const driveConnected = params.get('driveConnected');
    const reason = params.get('reason');

    if (driveConnected === 'true') {
      setAlert({ severity: 'success', message: 'Google Drive connected successfully!' });
      navigate('/settings', { replace: true, state: { tab: 5 } });
    } else if (driveConnected === 'false') {
      const msg = reason === 'no_refresh_token'
        ? 'No refresh token received. Please revoke access at myaccount.google.com/permissions and try again.'
        : 'Google Drive connection failed. Please try again.';
      setAlert({ severity: 'error', message: msg });
      navigate('/settings', { replace: true, state: { tab: 5 } });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleConnect = () => {
    setConnecting(true);
    settingsService.connectGoogleDrive(); // browser redirects — page unloads
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await settingsService.disconnectGoogleDrive();
      setDriveStatus({ connected: false, email: null, folderId: '' });
      setAlert({ severity: 'info', message: 'Google Drive disconnected.' });
    } catch {
      setAlert({ severity: 'error', message: 'Failed to disconnect Google Drive.' });
    } finally {
      setDisconnecting(false);
    }
  };





  const handleDownload = async () => {
    try {
      setDownloading(true);
      await settingsService.downloadBackup(downloadRange.from, downloadRange.to);
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Failed to download backup.' });
    } finally {
      setDownloading(false);
    }
  };

  const set = (field, value) => setBackupForm(prev => ({ ...prev, [field]: value }));

  // ── render ─────────────────────────────────────────────────────────────────
  if (loadingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alert banner */}
      {alert && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert(null)}
          sx={{ mb: 2, borderRadius: 0 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — Download Backup
      ══════════════════════════════════════════════════════════════════ */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DownloadIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('Settings.Download_Backup')}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('Settings.Beckup_Description')}
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label={t('Reports.From_Date')}
              value={downloadRange.from}
              onChange={e => setDownloadRange(prev => ({ ...prev, from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: downloadRange.to }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label={t('Reports.To_Date')}
              value={downloadRange.to}
              onChange={e => setDownloadRange(prev => ({ ...prev, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: downloadRange.from, max: today }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleDownload}
              disabled={downloading || !downloadRange.from || !downloadRange.to}
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              sx={{ borderRadius: 0, textTransform: 'none', height: '40px' }}
            >
              {downloading ? t('Settings.Downloading') : t('Settings.Download_Backup')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BackupIcon color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
           {t('Settings.Cloud_Backup')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* status icon */}
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: driveStatus.connected ? 'success.light' : 'action.hover',
          }}>
            {driveStatus.connected
              ? <CloudDoneIcon sx={{ color: 'success.dark' }} />
              : <CloudOffIcon sx={{ color: 'text.disabled' }} />}
          </Box>

          {/* status text */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('Settings.Google_Drive')}
            </Typography>
            {driveStatus.connected ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label="Connected"
                  sx={{ borderRadius: 0, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {driveStatus.email}
                </Typography>
                {lastBackup && (
                  <Typography variant="body2" color="text.secondary">
                    · Last backup: {new Date(lastBackup).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not connected — link your Google account to enable cloud backups.
              </Typography>
            )}
          </Box>

          {/* action buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {driveStatus.connected ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleDisconnect}
                disabled={disconnecting}
                startIcon={disconnecting ? <CircularProgress size={14} /> : <CloudOffIcon />}
                sx={{ borderRadius: 0, textTransform: 'none' }}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={handleConnect}
                disabled={connecting}
                startIcon={connecting ? <CircularProgress size={14} color="inherit" /> : <GoogleIcon />}
                sx={{ borderRadius: 0, textTransform: 'none' }}
              >
                {connecting ? 'Redirecting...' : 'Connect Google Drive'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScheduleIcon color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('Settings.Backup_Schedule')}
          </Typography>
        </Box>

        {!driveStatus.connected && (
          <Alert severity="info" sx={{ borderRadius: 0, mb: 2 }}>
            Connect your Google Drive account above to configure and enable scheduled backups.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={backupForm.enabled}
                onChange={e => set('enabled', e.target.checked)}
                color="primary"
                disabled={!driveStatus.connected}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: !driveStatus.connected ? 'text.disabled' : 'inherit' }}>
                  {t('Settings.Google_Drive')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('Settings.Automatically_upload')}
                </Typography>
              </Box>
            }
          />
        </Box>

        {backupForm.enabled && driveStatus.connected && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel id="frequency-label">{t('Settings.Frequency')}</InputLabel>
                <Select
                  labelId="frequency-label"
                  id="frequency-select"
                  value={backupForm.frequency || 'daily'}
                  onChange={e => set('frequency', e.target.value)}
                  label={t('Settings.Frequency')}
                  sx={{ borderRadius: 0 }}
                >
                  <MenuItem value="daily">{t('Settings.Daily')}</MenuItem>
                  <MenuItem value="weekly">{t('Settings.Weekly')}</MenuItem>
                  <MenuItem value="monthly">{t('Settings.Monthly')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                fullWidth
                type="time"
                label={t('Settings.Time_of_Day')}
                value={backupForm.timeOfDay}
                onChange={e => set('timeOfDay', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
            </Grid>

            {backupForm.frequency === 'weekly' && (
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    value={backupForm.dayOfWeek}
                    onChange={e => set('dayOfWeek', e.target.value)}
                    label="Day of Week"
                    sx={{ borderRadius: 0 }}
                  >
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                      <MenuItem key={i} value={i}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {backupForm.frequency === 'monthly' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Day of Month (1–28)"
                  value={backupForm.dayOfMonth}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '') {
                      set('dayOfMonth', '');
                    } else {
                      set('dayOfMonth', Math.min(28, Math.max(1, Number(val))));
                    }
                  }}
                  inputProps={{ min: 1, max: 28 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </Box>
  );
}